// File: src/components/admin/plans/PlanCostPanel.js
// live price / COGS / margin readout for the plan editor.
//
// Debounced 300ms on the draft prop. Calls POST /admin/plans/:id/estimate
// and colour-codes margin per spec:
//   - >=70% green
//   - 30-70% amber
//   - <30% red
//   - free tier (price=0) neutral
//   - unlimited AI (aiUnbounded) neutral with "unbounded" copy.

import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, Typography, Stack, Box, Chip, Tooltip } from '@mui/material';
import { mainApi } from '../../../services/mainApi';

function marginColor(pct) {
    if (pct == null) return 'default';
    if (pct >= 70) return 'success';
    if (pct >= 30) return 'warning';
    return 'error';
}

function formatUsd(n) {
    if (n == null) return '—';
    if (!Number.isFinite(n)) return '∞';
    return `$${n.toFixed(2)}`;
}

export default function PlanCostPanel({ planId, draft }) {
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                setError(null);
                const s = await mainApi.estimatePlan(planId, draft);
                setSummary(s);
            } catch (e) {
                setError(e?.response?.data?.error || e.message);
            }
        }, 300);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [planId, draft]);

    const aiUnbounded = summary?.aiUnbounded;
    const isFree = Number(draft?.price ?? 0) === 0;

    return (
        <Card variant="outlined" data-testid={`cost-panel-${planId}`}>
            <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Live cost & margin
                </Typography>

                <Stack spacing={1}>
                    <Row label="Price">
                        <Typography data-testid={`cost-panel-price-${planId}`}>
                            {formatUsd(summary?.priceUsd)}
                        </Typography>
                    </Row>

                    <Row label="Est. COGS at cap">
                        <Tooltip title={aiUnbounded
                            ? 'Plan has unlimited AI actions — COGS is unbounded.'
                            : 'Assumes a user saturates their monthly cap.'}>
                            <Typography data-testid={`cost-panel-cogs-${planId}`}>
                                {aiUnbounded ? 'Unbounded' : formatUsd(summary?.cogsUsd)}
                            </Typography>
                        </Tooltip>
                    </Row>

                    <Row label="Est. margin">
                        {aiUnbounded || isFree ? (
                            <Chip
                                size="small"
                                label={aiUnbounded ? 'Unbounded' : 'Free tier — COGS only'}
                                data-testid={`cost-panel-margin-${planId}`}
                            />
                        ) : (
                            <Chip
                                size="small"
                                color={marginColor(summary?.marginPct)}
                                label={summary?.marginPct != null ? `${summary.marginPct.toFixed(1)}%` : '—'}
                                data-testid={`cost-panel-margin-${planId}`}
                            />
                        )}
                    </Row>

                    {summary?.breakdown && !aiUnbounded && (
                        <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Breakdown
                            </Typography>
                            <Row label="AI"><Typography variant="body2">{formatUsd(summary.breakdown.aiUsd)}</Typography></Row>
                            <Row label="Storage"><Typography variant="body2">{formatUsd(summary.breakdown.storageUsd)}</Typography></Row>
                            <Row label="API"><Typography variant="body2">{formatUsd(summary.breakdown.apiUsd)}</Typography></Row>
                        </Box>
                    )}

                    {error && (
                        <Typography color="error" variant="caption">{error}</Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function Row({ label, children }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            {children}
        </Box>
    );
}
